import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { pdf } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useColumnSelection from '~hooks/useColumnSelection/useColumnSelection';
import { useCompany } from '~hooks/useCompany/useCompany';
// Bundled brand asset because cdn.tread.io doesn't currently send CORS
// headers, which imageUrlToBase64 requires. See quote PDF for the same pattern.
import treadLogoPng from '~images/tread_logo_horizontal.png';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { ExportColumn, PDF_EXPORT_MAX_ROWS } from '~utils/export/exportColumn';
import { downloadBlobFile, toCsv } from '~utils/export/toCsv';
import { imageUrlToBase64 } from '~utils/image/imageToBase64';

import ExportTablePdf from './ExportTablePdf';

export type ExportFormat = 'csv' | 'pdf';

interface ExportMenuButtonPdfHeaderMeta {
  companyId: string;
  companyName: string;
  filterDateLabel?: string;
}

interface ExportMenuButtonProps<T> {
  columns: ExportColumn<T>[];
  disabledTooltip?: string;
  fetchRows: () => Promise<T[]>;
  footerAction?: ReactNode | ((closeMenu: () => void) => ReactNode);
  isDisabled?: boolean;
  isExportActionDisabled?: boolean;
  label: string;
  makeFilename: (extension: ExportFormat) => string;
  pdfHeaderMeta?: ExportMenuButtonPdfHeaderMeta;
  pdfTitle: string;
  surfaceId: string;
}

interface SortableColumnMenuItemProps<T> {
  column: ExportColumn<T>;
  isSelected: boolean;
  onToggle: (columnId: string) => void;
}

function SortableColumnMenuItem<T>({
  column,
  isSelected,
  onToggle,
}: SortableColumnMenuItemProps<T>) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: column.id,
    disabled: !isSelected,
  });

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onToggle(column.id);
    },
    [column.id, onToggle],
  );

  return (
    <MenuItem
      ref={setNodeRef}
      data-test-id={`export-column-${column.id}`}
      onClick={handleClick}
      sx={{
        gap: 1,
        minHeight: 34,
        opacity: isSelected ? 1 : 0.65,
        px: 1,
        py: 0.5,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        onClick={(event) => event.stopPropagation()}
        sx={{
          color: isSelected
            ? theme.brandV2.colors.treadGray6
            : theme.brandV2.colors.treadGray7,
          cursor: isSelected ? 'grab' : 'default',
          display: 'flex',
        }}
      >
        <DragIndicator sx={{ fontSize: 18 }} />
      </Box>
      <Tooltip
        title={column.displayHeader ?? column.header}
        placement="right"
        disableInteractive
      >
        <FormControlLabel
          label={column.displayHeader ?? column.header}
          control={
            <Checkbox
              checked={isSelected}
              size="small"
              sx={{
                '&.MuiCheckbox-root': {
                  color: theme.brandV2.colors.treadGray6,
                  py: 0,
                },
                '&.Mui-checked': { color: theme.brandV2.colors.treadOrange },
              }}
            />
          }
          sx={{
            '&.MuiFormControlLabel-root': {
              mr: 0,
              overflow: 'hidden',
              width: '100%',
              '& .MuiFormControlLabel-label': {
                color: theme.brandV2.colors.treadBlack,
                fontSize: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              },
            },
          }}
        />
      </Tooltip>
      {!column.defaultSelected && (
        <Chip
          label={t('export.new_column')}
          size="small"
          sx={{
            bgcolor: theme.brandV2.colors.treadGreen,
            color: 'white',
            flexShrink: 0,
            fontSize: '9px',
            fontWeight: 700,
            height: 18,
          }}
        />
      )}
    </MenuItem>
  );
}

export default function ExportMenuButton<T>({
  columns,
  disabledTooltip,
  fetchRows,
  footerAction,
  isDisabled,
  isExportActionDisabled,
  label,
  makeFilename,
  pdfHeaderMeta,
  pdfTitle,
  surfaceId,
}: ExportMenuButtonProps<T>) {
  const theme = useTheme();
  const { toasterStore } = useStores();
  const { getCompanyLogoBase64 } = useCompany();
  const menuHandler = useRef<MenuHandler>(null);
  const { move, orderedSelectedColumns, reset, selectAll, selectedIds, toggle } =
    useColumnSelection(surfaceId, columns);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [isExporting, setIsExporting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
  );

  const hasSelectedColumns = orderedSelectedColumns.length > 0;
  const isExportUnavailable = isDisabled || isExportActionDisabled;
  const isExportDisabled = isExportUnavailable || isExporting || !hasSelectedColumns;
  const tooltipIsVisible = Boolean(isExportUnavailable && disabledTooltip);

  const displayColumns = useMemo(
    () => [
      ...orderedSelectedColumns,
      ...columns.filter((column) => !selectedIds.includes(column.id)),
    ],
    [columns, orderedSelectedColumns, selectedIds],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = selectedIds.indexOf(String(active.id));
      const newIndex = selectedIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      move(oldIndex, newIndex);
    },
    [move, selectedIds],
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const rows = await fetchRows();
      if (rows.length === 0) {
        toasterStore.push(alert(t('export.no_rows_to_export'), AlertTypes.info));
        return;
      }

      if (format === 'pdf') {
        const isAbovePdfRowCap = rows.length > PDF_EXPORT_MAX_ROWS;
        if (isAbovePdfRowCap) {
          toasterStore.push(alert(t('export.pdf_row_cap_exceeded'), AlertTypes.error));
          return;
        }

        const [companyLogoBase64, treadLogoBase64] = await Promise.all([
          pdfHeaderMeta
            ? getCompanyLogoBase64(pdfHeaderMeta.companyId)
            : Promise.resolve(null),
          imageUrlToBase64(treadLogoPng),
        ]);
        if (!treadLogoBase64) {
          console.warn(
            `Failed to convert image to base64 for export PDF: ${treadLogoPng}`,
          );
        }

        const blob = await pdf(
          <ExportTablePdf
            columns={orderedSelectedColumns}
            headerMeta={{
              companyLogoBase64: companyLogoBase64 ?? undefined,
              companyName: pdfHeaderMeta?.companyName ?? '',
              filterDateLabel: pdfHeaderMeta?.filterDateLabel,
              generatedAt: dayjs.tz().format('MMM D, YYYY h:mm A'),
              treadLogoBase64: treadLogoBase64 ?? undefined,
            }}
            rows={rows}
            title={pdfTitle}
          />,
        ).toBlob();
        downloadBlobFile(blob, makeFilename('pdf'));
      } else {
        const csv = toCsv(rows, orderedSelectedColumns);
        downloadBlobFile(
          new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
          makeFilename('csv'),
        );
      }

      toasterStore.push(
        alert(t('export.export_success', { count: rows.length }), AlertTypes.success),
      );
      menuHandler.current?.onClose?.();
    } catch (error) {
      console.error('Export failed:', error);
      toasterStore.push(alert(t('export.export_failed'), AlertTypes.error));
    } finally {
      setIsExporting(false);
    }
  }, [
    fetchRows,
    format,
    getCompanyLogoBase64,
    makeFilename,
    orderedSelectedColumns,
    pdfHeaderMeta,
    pdfTitle,
    toasterStore,
  ]);

  const handleFormatChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextFormat: ExportFormat | null) => {
      if (nextFormat) {
        setFormat(nextFormat);
      }
    },
    [],
  );

  const handleOpenStateChanged = useCallback((isOpen: boolean) => {
    // Reset the transient format choice when the menu closes; the column selection is
    // intentionally persisted across opens.
    if (!isOpen) setFormat('csv');
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <Tooltip
        title={disabledTooltip ?? ''}
        disableHoverListener={!tooltipIsVisible}
        disableFocusListener={!tooltipIsVisible}
        disableTouchListener={!tooltipIsVisible}
      >
        <span
          data-test-id="export-menu-button-primary-tooltip-anchor"
          style={{ display: 'flex' }}
        >
          <SmallLoadingButton
            color="inherit"
            disabled={isExportDisabled}
            loading={isExporting}
            onClick={handleExport}
            variant="outlined"
            sx={{
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderBottomRightRadius: 0,
              borderRight: 0,
              borderTopRightRadius: 0,
            }}
          >
            {label}
          </SmallLoadingButton>
        </span>
      </Tooltip>
      <Menu
        ref={menuHandler}
        disableAutoFocusItem
        keepMounted
        menuTrigger={
          <SmallButton
            aria-label={t('export.columns_title')}
            color="inherit"
            disabled={Boolean(isDisabled)}
            variant="outlined"
            sx={{
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
              minWidth: 28,
              px: 0.5,
            }}
          >
            <ArrowDropDown sx={{ fontSize: 18 }} />
          </SmallButton>
        }
        onOpenStateChanged={handleOpenStateChanged}
        sx={{
          '& .MuiPaper-root': {
            width: 340,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {t('export.columns_title')}
            </Typography>
            <Typography sx={{ color: theme.brandV2.colors.treadGray6, fontSize: 12 }}>
              {t('export.selected_count', {
                count: orderedSelectedColumns.length,
              })}
            </Typography>
          </Box>
          <Typography sx={{ color: theme.brandV2.colors.treadGray6, fontSize: 12 }}>
            {t('export.columns_hint')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            onChange={handleFormatChange}
            size="small"
            value={format}
            sx={{ mt: 1 }}
          >
            <ToggleButton value="csv" sx={{ fontSize: 12 }}>
              {t('export.csv')}
            </ToggleButton>
            <ToggleButton value="pdf" sx={{ fontSize: 12 }}>
              {t('export.pdf')}
            </ToggleButton>
          </ToggleButtonGroup>
          {format === 'pdf' && (
            <Typography
              sx={{ color: theme.brandV2.colors.treadGray6, fontSize: 11, mt: 0.5 }}
            >
              {t('export.pdf_row_cap_caption')}
            </Typography>
          )}
          <Box sx={{ maxHeight: 300, mt: 1, overflow: 'auto' }}>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                {displayColumns.map((column) => (
                  <SortableColumnMenuItem
                    key={column.id}
                    column={column}
                    isSelected={selectedIds.includes(column.id)}
                    onToggle={toggle}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              mt: 1,
              pt: 1,
            }}
          >
            <SmallButton color="inherit" onClick={selectAll} variant="text">
              {t('export.select_all')}
            </SmallButton>
            <SmallButton color="inherit" onClick={reset} variant="text">
              {t('export.reset')}
            </SmallButton>
            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={isExportDisabled}
              loading={isExporting}
              onClick={handleExport}
              variant="contained"
            >
              {t('actions.export')}
            </SmallLoadingButton>
          </Box>
          <Box
            sx={{
              '&:empty': { display: 'none' },
              borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              mt: 1,
              pt: 0.5,
            }}
          >
            {typeof footerAction === 'function'
              ? footerAction(() => menuHandler.current?.onClose?.())
              : footerAction}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
}
